import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Button from '@mui/material/Button';
import LinearProgress from '@mui/material/LinearProgress';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import Snackbar from '@mui/material/Snackbar';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SendIcon from '@mui/icons-material/Send';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import {
  getMyBooking,
  getAssessmentTemplate,
  saveAssessmentDraft,
  getAssessmentUploadUrl,
  resubmitAssessment,
} from '../services/internApi';
import api from '../services/api';
import QuestionRenderer from '../components/assessment/QuestionRenderer';

const AUTO_SAVE_MS = 15000;

// Evaluates critical-finding rule for a response; mirror of the helper in
// QuestionRenderer, duplicated here to avoid cross-file coupling.
function isCritical(question, value) {
  const rule = question.critical_if;
  if (!rule || value == null || value === '') return false;
  if (rule.yes === true && value === 'yes') return true;
  if (rule.no === true && value === 'no') return true;
  if (typeof rule.rating_lte === 'number' && Number(value) <= rule.rating_lte) return true;
  if (Array.isArray(rule.includes) && Array.isArray(value)) {
    return value.some((v) => rule.includes.includes(v));
  }
  return false;
}

function isAnswered(question, response) {
  if (!response) return false;
  const v = response.value;
  if (v == null || v === '') return false;
  if (Array.isArray(v) && v.length === 0) return false;
  // Rating <= 2 requires a note
  if (question.type === 'rating_1_5' && typeof v === 'number' && v <= 2) {
    return Boolean((response.notes || '').trim());
  }
  return true;
}

export default function AssessmentPage() {
  const { bookingId } = useParams();
  const navigate = useNavigate();

  const [booking, setBooking] = useState(null);
  const [template, setTemplate] = useState(null);
  const [responses, setResponses] = useState({});
  const [expanded, setExpanded] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [submitConfirmed, setSubmitConfirmed] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [snack, setSnack] = useState({ open: false, message: '', severity: 'success' });
  const saveTimer = useRef(null);
  const initialLoad = useRef(true);

  // Load booking + template
  useEffect(() => {
    (async () => {
      try {
        const b = await getMyBooking(bookingId);
        setBooking(b);
        const templateId = b.service_type === 'business' ? 'business' : 'home';
        const { template: t } = await getAssessmentTemplate(templateId);
        setTemplate(t);
        // Hydrate existing responses if any
        const existing = b.assessment_data?.responses || {};
        setResponses(existing);
        // Auto-expand the first unfinished category
        const firstUnfinished = (t.categories || []).find((c) =>
          c.questions.some((q) => !isAnswered(q, existing[q.id]))
        );
        setExpanded(firstUnfinished?.id || t.categories?.[0]?.id || null);
      } catch (err) {
        setError(err?.response?.data?.error || err.message || 'Failed to load assessment');
      } finally {
        setLoading(false);
        initialLoad.current = false;
      }
    })();
  }, [bookingId]);

  const handleResponseChange = useCallback((qid, value) => {
    setResponses((prev) => ({ ...prev, [qid]: value }));
  }, []);

  // Auto-save when responses change (debounced), but skip the initial hydration.
  useEffect(() => {
    if (initialLoad.current || !template) return;
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(async () => {
      setSaving(true);
      try {
        await saveAssessmentDraft({
          bookingId,
          templateId: template.template_id,
          templateVersion: template.version,
          responses,
          categoryId: expanded,
        });
        setLastSaved(new Date());
      } catch (err) {
        setSnack({
          open: true,
          message: err?.response?.data?.error || 'Draft save failed',
          severity: 'warning',
        });
      } finally {
        setSaving(false);
      }
    }, AUTO_SAVE_MS);
    return () => saveTimer.current && clearTimeout(saveTimer.current);
  }, [responses, expanded, template, bookingId]);

  const uploadPhoto = useCallback(
    async (file, questionId) => {
      const { upload_url, public_url } = await getAssessmentUploadUrl(bookingId, questionId);
      const res = await fetch(upload_url, {
        method: 'PUT',
        headers: { 'Content-Type': 'image/jpeg' },
        body: file,
      });
      if (!res.ok) throw new Error(`Upload failed (${res.status})`);
      return public_url;
    },
    [bookingId]
  );

  const { progress, requiredOutstanding, criticalCount, totalQuestions } = useMemo(() => {
    if (!template) return { progress: 0, requiredOutstanding: [], criticalCount: 0, totalQuestions: 0 };
    let answered = 0;
    let total = 0;
    let critical = 0;
    const outstanding = [];
    for (const cat of template.categories) {
      for (const q of cat.questions) {
        total += 1;
        if (isAnswered(q, responses[q.id])) answered += 1;
        else if (q.required) outstanding.push(`${q.number} ${q.prompt}`);
        if (isCritical(q, responses[q.id]?.value)) critical += 1;
      }
    }
    return {
      progress: total === 0 ? 0 : Math.round((answered / total) * 100),
      requiredOutstanding: outstanding,
      criticalCount: critical,
      totalQuestions: total,
    };
  }, [template, responses]);

  const isRevision = booking?.status === 'needs_revision';

  const handleSubmit = async () => {
    if (!submitConfirmed) return;
    setSubmitting(true);
    try {
      if (isRevision) {
        // Revision path: single API call that handles everything server-side
        // (recomputes criticals, resets the assessment to pending_review,
        // fires the mentor notification).
        await resubmitAssessment({
          bookingId,
          responses,
          templateId: template.template_id,
          templateVersion: template.version,
        });
        setSnack({ open: true, message: 'Resubmitted for mentor review.', severity: 'success' });
        setTimeout(() => navigate('/bookings'), 1200);
        return;
      }

      // First-time submit path
      await saveAssessmentDraft({
        bookingId,
        templateId: template.template_id,
        templateVersion: template.version,
        responses,
      });
      const critical_findings = [];
      const category_results = {};
      for (const cat of template.categories) {
        const catScore = { questions: {} };
        for (const q of cat.questions) {
          const r = responses[q.id];
          if (!r) continue;
          catScore.questions[q.id] = r;
          if (isCritical(q, r.value)) {
            critical_findings.push({
              question_id: q.id,
              number: q.number,
              prompt: q.prompt,
              value: r.value,
              notes: r.notes,
            });
          }
        }
        category_results[cat.id] = catScore;
      }
      await api.post('/assessment/submit_assessment', {
        action: 'submit_assessment',
        assessment_id: booking.assessment_id,
        intern_id: booking.intern_id,
        assessment_data: {
          responses,
          categories: category_results,
          critical_findings,
          template_id: template.template_id,
          template_version: template.version,
        },
      });
      setSnack({ open: true, message: 'Submitted for mentor review.', severity: 'success' });
      setTimeout(() => navigate('/bookings'), 1200);
    } catch (err) {
      setSnack({
        open: true,
        message: err?.response?.data?.error || err.message || 'Submit failed',
        severity: 'error',
      });
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
        <CircularProgress />
      </Box>
    );
  }
  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }
  if (!template) return null;

  return (
    <Box>
      <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/bookings')} sx={{ mb: 2 }}>
        Back to bookings
      </Button>

      {isRevision && (
        <Alert
          severity="warning"
          sx={{ mb: 2, alignItems: 'flex-start' }}
        >
          <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.5 }}>
            Mentor requested revisions
          </Typography>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Read the feedback below, update the flagged answers, and submit again. Your previous answers are still here — you only need to change what the mentor called out.
          </Typography>
          {booking?.mentor_feedback && (
            <Box
              sx={{
                backgroundColor: 'rgba(255,255,255,0.6)',
                borderRadius: 1,
                p: 1.5,
                whiteSpace: 'pre-wrap',
                fontSize: '0.9rem',
                lineHeight: 1.55,
                border: '1px solid rgba(237,108,2,0.25)',
              }}
            >
              {booking.mentor_feedback}
            </Box>
          )}
        </Alert>
      )}

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h4" sx={{ mb: 1 }}>{template.title}</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {template.description}
        </Typography>
        <Divider sx={{ my: 2 }} />
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
          <Typography variant="body2" sx={{ minWidth: 90 }}>
            Progress: <b>{progress}%</b>
          </Typography>
          <LinearProgress
            variant="determinate"
            value={progress}
            sx={{ flex: 1, height: 8, borderRadius: 4 }}
          />
          <Typography variant="caption" color="text.secondary" sx={{ minWidth: 80, textAlign: 'right' }}>
            {saving ? 'Saving…' : lastSaved ? `Saved ${lastSaved.toLocaleTimeString()}` : 'Not yet saved'}
          </Typography>
        </Box>
        {criticalCount > 0 && (
          <Chip
            icon={<WarningAmberIcon />}
            label={`${criticalCount} critical finding${criticalCount === 1 ? '' : 's'}`}
            color="error"
            size="small"
            sx={{ mt: 1 }}
          />
        )}
      </Paper>

      {template.categories.map((cat) => {
        const catAnswered = cat.questions.filter((q) => isAnswered(q, responses[q.id])).length;
        const isComplete = catAnswered === cat.questions.length;
        return (
          <Accordion
            key={cat.id}
            expanded={expanded === cat.id}
            onChange={(_, isExpanded) => setExpanded(isExpanded ? cat.id : null)}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                {isComplete && <CheckCircleIcon color="success" fontSize="small" />}
                <Typography sx={{ fontWeight: 600, flex: 1 }}>{cat.title}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {catAnswered} / {cat.questions.length}
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              {cat.description && (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {cat.description}
                </Typography>
              )}
              {cat.questions.map((q) => (
                <QuestionRenderer
                  key={q.id}
                  question={q}
                  response={responses[q.id]}
                  onChange={(v) => handleResponseChange(q.id, v)}
                  uploadPhoto={uploadPhoto}
                />
              ))}
            </AccordionDetails>
          </Accordion>
        );
      })}

      <Paper sx={{ p: 3, mt: 3, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box>
          <Typography variant="body2">
            {progress < 100
              ? `${totalQuestions - Math.round((progress / 100) * totalQuestions)} of ${totalQuestions} questions remaining`
              : 'All questions answered'}
          </Typography>
          {requiredOutstanding.length > 0 && (
            <Typography variant="caption" color="error">
              Required not yet answered: {requiredOutstanding.length}
            </Typography>
          )}
        </Box>
        <Button
          variant="contained"
          size="large"
          startIcon={<SendIcon />}
          onClick={() => setReviewOpen(true)}
          disabled={requiredOutstanding.length > 0}
        >
          {isRevision ? 'Review & Resubmit' : 'Review & Submit'}
        </Button>
      </Paper>

      <Dialog open={reviewOpen} onClose={() => { setReviewOpen(false); setSubmitConfirmed(false); }} maxWidth="md" fullWidth>
        <DialogTitle>{isRevision ? 'Review Before Resubmitting' : 'Review Before Submitting'}</DialogTitle>
        <DialogContent dividers>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Once submitted, this assessment goes to a CWC mentor for review and cannot be edited by you. The mentor may send it back with feedback if revisions are needed.
          </Alert>
          <Typography variant="body2" sx={{ mb: 2 }}>
            <b>{progress}%</b> complete — {criticalCount} critical finding{criticalCount === 1 ? '' : 's'}. Scroll through your answers below to confirm before submitting.
          </Typography>
          {template.categories.map((cat) => (
            <Box key={cat.id} sx={{ mb: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                {cat.title}
              </Typography>
              {cat.questions.map((q) => {
                const r = responses[q.id];
                const answered = isAnswered(q, r);
                const critical = isCritical(q, r?.value);
                return (
                  <Box
                    key={q.id}
                    sx={{
                      py: 0.5,
                      borderLeft: critical ? '3px solid' : '1px solid',
                      borderColor: critical ? 'error.main' : 'divider',
                      pl: 1.5,
                      mb: 0.5,
                    }}
                  >
                    <Typography variant="caption" color="text.secondary">
                      {q.number}
                    </Typography>
                    <Typography variant="body2">{q.prompt}</Typography>
                    <Typography
                      variant="body2"
                      sx={{ color: answered ? 'text.primary' : 'text.disabled', fontWeight: answered ? 500 : 400 }}
                    >
                      → {answered ? formatValue(r) : '(not answered)'}
                      {r?.notes && <Box component="span" sx={{ color: 'text.secondary', fontStyle: 'italic' }}> — {r.notes}</Box>}
                    </Typography>
                  </Box>
                );
              })}
            </Box>
          ))}
          <FormControlLabel
            sx={{ mt: 2 }}
            control={
              <Checkbox
                checked={submitConfirmed}
                onChange={(e) => setSubmitConfirmed(e.target.checked)}
              />
            }
            label="I confirm my responses are accurate and ready for mentor review."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setReviewOpen(false); setSubmitConfirmed(false); }}>Back to editing</Button>
          <Button
            variant="contained"
            color="success"
            startIcon={<SendIcon />}
            onClick={handleSubmit}
            disabled={!submitConfirmed || submitting}
          >
            {submitting
              ? <CircularProgress size={20} color="inherit" />
              : isRevision ? 'Resubmit for Mentor Review' : 'Submit for Mentor Review'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snack.open}
        autoHideDuration={4000}
        onClose={() => setSnack({ ...snack, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snack.severity} onClose={() => setSnack({ ...snack, open: false })}>
          {snack.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

function formatValue(response) {
  const v = response.value;
  if (Array.isArray(v)) return v.join(', ');
  if (typeof v === 'string' && v.startsWith('http')) return '📷 photo uploaded';
  return String(v);
}
